/**
 * Drawer navigator (the app shell's sidebar).
 *
 * Mirrors @react-navigation/drawer 6.5 as it rendered on the web with
 * `drawerType: 'front'`: visited screens stay mounted and are hidden when not
 * focused, the drawer slides over the content with a dimming overlay, the
 * overlay and Escape close it, and opening it is part of the navigation state
 * (so it is also a browser history entry).
 */
import * as React from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import {
  Descriptor,
  Group,
  Screen,
  useNavigationBuilder
} from '../core/NavigationBuilder'
import {
  DrawerActions,
  DrawerRouter,
  NavigationState,
  getDrawerStatusFromState
} from '../core/routers'

const BACKGROUND_COLOR = 'rgb(242, 242, 242)'
const CARD_COLOR = 'rgb(255, 255, 255)'
const OVERLAY_COLOR = 'rgba(0, 0, 0, 0.5)'
const PROGRESS_EPSILON = 0.05

interface DrawerNavigatorProps {
  id?: string
  initialRouteName?: string
  backBehavior?: 'initialRoute' | 'firstRoute' | 'history' | 'order' | 'none'
  defaultStatus?: 'open' | 'closed'
  screenOptions?: object | ((props: any) => object)
  drawerContent?: (props: {
    state: NavigationState
    navigation: any
    descriptors: Record<string, Descriptor>
  }) => React.ReactNode
  children: React.ReactNode
  // Unknown navigator props are ignored, as react-navigation did.
  [prop: string]: any
}

function DrawerNavigator({
  id,
  initialRouteName,
  backBehavior,
  defaultStatus = 'closed',
  screenOptions,
  drawerContent,
  children
}: DrawerNavigatorProps) {
  const { state, descriptors, navigation } = useNavigationBuilder(
    DrawerRouter,
    {
      id,
      initialRouteName,
      backBehavior,
      defaultStatus,
      children,
      screenOptions
    }
  )

  return (
    <DrawerView
      state={state}
      descriptors={descriptors}
      navigation={navigation}
      defaultStatus={defaultStatus}
      drawerContent={drawerContent}
    />
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function createDrawerNavigator<
  ParamList = Record<string, object | undefined>
>() {
  return { Navigator: DrawerNavigator, Screen, Group }
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

function DrawerView({
  state,
  navigation,
  descriptors,
  defaultStatus,
  drawerContent
}: {
  state: NavigationState
  navigation: any
  descriptors: Record<string, Descriptor>
  defaultStatus: 'open' | 'closed'
  drawerContent?: DrawerNavigatorProps['drawerContent']
}) {
  const focusedRouteKey = state.routes[state.index].key
  const { drawerStyle } = descriptors[focusedRouteKey].options as {
    drawerStyle?: any
  }

  const [loaded, setLoaded] = React.useState([focusedRouteKey])

  if (!loaded.includes(focusedRouteKey)) {
    setLoaded([...loaded, focusedRouteKey])
  }

  const drawerStatus = getDrawerStatusFromState(state)

  const handleDrawerOpen = React.useCallback(() => {
    navigation.dispatch({ ...DrawerActions.openDrawer(), target: state.key })
  }, [navigation, state.key])

  const handleDrawerClose = React.useCallback(() => {
    navigation.dispatch({ ...DrawerActions.closeDrawer(), target: state.key })
  }, [navigation, state.key])

  React.useEffect(() => {
    if (drawerStatus === defaultStatus) {
      return undefined
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && navigation.isFocused()) {
        if (defaultStatus === 'open') {
          handleDrawerOpen()
        } else {
          handleDrawerClose()
        }
      }
    }

    document?.body?.addEventListener?.('keyup', handleEscape)

    return () => {
      document?.body?.removeEventListener?.('keyup', handleEscape)
    }
  }, [
    defaultStatus,
    drawerStatus,
    handleDrawerClose,
    handleDrawerOpen,
    navigation
  ])

  const scenes = (
    <View style={drawerViewStyles.content}>
      {state.routes.map((route, index) => {
        const descriptor = descriptors[route.key]
        const isFocused = state.index === index

        if (!loaded.includes(route.key) && !isFocused) {
          return null
        }

        return (
          <View
            key={route.key}
            style={[
              StyleSheet.absoluteFill,
              { zIndex: isFocused ? 0 : -1 },
              { display: isFocused ? 'flex' : 'none' }
            ]}
          >
            <View
              style={[
                { flex: 1, backgroundColor: BACKGROUND_COLOR },
                [screenStyles.container, undefined]
              ]}
            >
              <View style={screenStyles.content}>{descriptor.render()}</View>
            </View>
          </View>
        )
      })}
    </View>
  )

  const content = drawerContent
    ? drawerContent({ state, navigation, descriptors })
    : null

  return (
    <View style={[safeAreaStyles.container, undefined]}>
      <View style={drawerViewStyles.content}>
        <Drawer
          open={drawerStatus !== 'closed'}
          onOpen={handleDrawerOpen}
          onClose={handleDrawerClose}
          drawerWidth={StyleSheet.flatten(drawerStyle)?.width ?? 240}
          drawerStyle={drawerStyle}
          scenes={scenes}
          content={content}
        />
      </View>
    </View>
  )
}

// Spring used by the drawer (stiffness 1000, damping 500, mass 3). Like
// react-native-reanimated's spring, an over-damped configuration is animated as
// critically damped, which settles in about 750 ms.
const OMEGA = Math.sqrt(1000 / 3)
const REST_THRESHOLD = 0.01

function useDrawerAnimation(target: number) {
  const [position, setPosition] = React.useState(target)
  const animation = React.useRef<{
    position: number
    velocity: number
    frame?: number
  }>({
    position: target,
    velocity: 0
  })

  React.useEffect(() => {
    const current = animation.current
    if (current.frame !== undefined) {
      cancelAnimationFrame(current.frame)
    }
    if (current.position === target && current.velocity === 0) {
      return undefined
    }

    const from = current.position
    const x0 = target - from
    const v0 = -current.velocity
    let start: number | undefined

    const step = (now: number) => {
      if (start === undefined) {
        start = now
      }
      const t = (now - start) / 1000
      const envelope = Math.exp(-OMEGA * t)
      let nextPosition = target - envelope * (x0 + (v0 + OMEGA * x0) * t)
      const nextVelocity =
        envelope * (v0 * (t * OMEGA - 1) + t * x0 * OMEGA * OMEGA)

      // overshootClamping
      const overshot =
        from < target ? nextPosition > target : nextPosition < target
      const atRest =
        Math.abs(nextVelocity) < REST_THRESHOLD &&
        Math.abs(target - nextPosition) < REST_THRESHOLD

      if (overshot || atRest) {
        nextPosition = target
        current.position = target
        current.velocity = 0
        current.frame = undefined
        setPosition(target)
        return
      }

      current.position = nextPosition
      current.velocity = nextVelocity
      setPosition(nextPosition)
      current.frame = requestAnimationFrame(step)
    }

    current.frame = requestAnimationFrame(step)

    return () => {
      if (current.frame !== undefined) {
        cancelAnimationFrame(current.frame)
        current.frame = undefined
      }
    }
  }, [target])

  return position
}

function Drawer({
  open,
  onOpen,
  onClose,
  drawerWidth,
  drawerStyle,
  scenes,
  content
}: {
  open: boolean
  onOpen: () => void
  onClose: () => void
  drawerWidth: number
  drawerStyle: any
  scenes: React.ReactNode
  content: React.ReactNode
}) {
  // translateX of the drawer: -drawerWidth when closed, 0 when open
  const translateX = useDrawerAnimation(open ? 0 : -drawerWidth)
  const progress = (translateX + drawerWidth) / drawerWidth
  const overlayActive = progress > PROGRESS_EPSILON

  const openRef = React.useRef(open)
  const toggleDrawer = React.useCallback(
    (nextOpen: boolean) => {
      openRef.current = nextOpen
      if (nextOpen) {
        onOpen()
      } else {
        onClose()
      }
    },
    [onOpen, onClose]
  )

  React.useEffect(() => toggleDrawer(open), [open, toggleDrawer])

  return (
    <View
      style={StyleSheet.flatten([drawerStyles.main, { flexDirection: 'row' }])}
    >
      <View
        style={StyleSheet.flatten([
          drawerStyles.content,
          { transform: [{ translateX: 0 }] }
        ])}
      >
        <View style={drawerStyles.content}>{scenes}</View>
        <View
          pointerEvents={overlayActive ? 'auto' : 'none'}
          style={StyleSheet.flatten([
            overlayStyles.overlay,
            { WebkitTapHighlightColor: 'transparent' } as any,
            { opacity: progress, zIndex: overlayActive ? 0 : -1 },
            { backgroundColor: OVERLAY_COLOR }
          ])}
        >
          <Pressable
            onPress={() => toggleDrawer(false)}
            style={overlayStyles.pressable}
          />
        </View>
      </View>
      <View
        style={StyleSheet.flatten([
          drawerStyles.container,
          { position: 'absolute', zIndex: 0 },
          { width: drawerWidth, backgroundColor: CARD_COLOR },
          drawerStyle,
          {
            opacity: 1,
            left: -drawerWidth,
            transform: [{ translateX: translateX + drawerWidth }]
          }
        ])}
      >
        {content}
      </View>
    </View>
  )
}

const safeAreaStyles = StyleSheet.create({
  container: {
    flex: 1
  }
})

const drawerViewStyles = StyleSheet.create({
  content: {
    flex: 1
  }
})

const screenStyles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column-reverse'
  },
  content: {
    flex: 1
  }
})

const drawerStyles = StyleSheet.create({
  container: {
    top: 0,
    bottom: 0,
    maxWidth: '100%',
    width: '80%'
  },
  content: {
    flex: 1
  },
  main: {
    flex: 1
  }
})

const overlayStyles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)'
  },
  pressable: {
    flex: 1
  }
})
